import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { FaEye, FaTrash, FaBan, FaUser, FaSearch, FaCheckCircle, FaTimesCircle, FaStore } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function AllVendor() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllVendors`);
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error("Fetch vendors error:", err);
      Swal.fire("Error", "Failed to fetch vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const confirm = await Swal.fire({
      title: `Confirm ${newStatus === "active" ? "Activation" : "Deactivation"}?`,
      text: `This will ${newStatus === "active" ? "enable" : "disable"} the vendor's ability to manage products.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newStatus === "active" ? "#10b981" : "#f59e0b",
      confirmButtonText: `Yes, ${newStatus}`,
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.put(`${baseurl}updateVendorStatus/${id}`, { status: newStatus });
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Vendor is now ${newStatus}`,
        showConfirmButton: false,
        timer: 2000,
      });
      fetchVendors();
    } catch (err) {
      Swal.fire("Error", "Update failed", "error");
    }
  };

  const deleteVendor = async (id) => {
    const confirm = await Swal.fire({
      title: "Permanent Delete?",
      text: "All associated data for this vendor will be removed. This cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Permanently",
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteVendor/${id}`);
        Swal.fire("Deleted!", "Vendor record removed.", "success");
        fetchVendors();
      } catch (err) {
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  const filteredVendors = vendors.filter((v) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch =
      v.name.toLowerCase().includes(searchLower) ||
      v.email.toLowerCase().includes(searchLower) ||
      v.phone.toLowerCase().includes(searchLower) ||
      v.business_name.toLowerCase().includes(searchLower);

    const matchesStatus = filters.status ? v.status === filters.status : true;
    const matchesFromDate = filters.fromDate ? new Date(v.created_at) >= new Date(filters.fromDate) : true;
    const matchesToDate = filters.toDate ? new Date(v.created_at) <= new Date(filters.toDate) : true;

    return matchesSearch && matchesStatus && matchesFromDate && matchesToDate;
  });

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-1">Vendor Directory</h4>
              <p className="text-muted small mb-0">Manage your partner ecosystem, monitor business status, and review merchant performance.</p>
            </div>
            <div className="d-flex gap-2">
                <div className="stat-card shadow-sm bg-white rounded-3 px-3 py-2 border-start border-primary border-4">
                    <small className="text-muted d-block extra-small fw-bold text-uppercase">Active Partners</small>
                    <span className="fw-bold text-dark">{vendors.filter(v => v.status === 'active').length} Merchants</span>
                </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="card border-0 shadow-sm mb-4 rounded-3">
            <div className="card-body p-2">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <div className="input-group input-group-sm border rounded-2 bg-light">
                    <span className="input-group-text bg-transparent border-0"><FaSearch className="text-muted" /></span>
                    <input
                      type="text"
                      className="form-control bg-transparent border-0"
                      placeholder="Search by Name, Business, Email..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select form-select-sm border-0 bg-light"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <option value="">Status: All</option>
                    <option value="active">Status: Active</option>
                    <option value="inactive">Status: Inactive</option>
                  </select>
                </div>
                <div className="col-md-4">
                    <div className="d-flex gap-2">
                        <Flatpickr
                        options={{ dateFormat: "Y-m-d" }}
                        placeholder="Joined After"
                        className="form-control form-control-sm border-0 bg-light text-center"
                        value={filters.fromDate}
                        onChange={(date) => setFilters({ ...filters, fromDate: date[0] })}
                        />
                        <Flatpickr
                        options={{ dateFormat: "Y-m-d" }}
                        placeholder="Joined Before"
                        className="form-control form-control-sm border-0 bg-light text-center"
                        value={filters.toDate}
                        onChange={(date) => setFilters({ ...filters, toDate: date[0] })}
                        />
                    </div>
                </div>
                <div className="col-md-2">
                    <button className="btn btn-sm btn-outline-secondary border-0 w-100 fw-bold" onClick={() => setFilters({search: "", status: "", fromDate: "", toDate: ""})}>Clear</button>
                </div>
              </div>
            </div>
          </div>

          {/* Vendor Table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0 vendor-table">
                <thead className="bg-white border-bottom">
                  <tr>
                    <th className="ps-4">Merchant Details</th>
                    <th>Business Profile</th>
                    <th>Contact Info</th>
                    <th>Account Status</th>
                    <th>Onboarding Date</th>
                    <th className="text-end pe-4">Management</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : filteredVendors.length > 0 ? (
                    filteredVendors.map((v, i) => (
                      <tr key={v.id}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle me-3">
                                {v.name.charAt(0)}
                            </div>
                            <div>
                                <div className="fw-bold text-dark small">{v.name}</div>
                                <div className="text-muted extra-small">ID: #VEN-{v.id}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                             <FaStore className="text-primary me-2" size={12} />
                             <span className="small fw-semibold">{v.business_name}</span>
                          </div>
                        </td>
                        <td>
                           <div className="small text-dark">{v.email}</div>
                           <div className="extra-small text-muted">{v.phone}</div>
                        </td>
                        <td>
                          <span className={`status-badge ${v.status === "active" ? "active" : "inactive"}`}>
                            {v.status === "active" ? <FaCheckCircle className="me-1" /> : <FaTimesCircle className="me-1" />}
                            {v.status}
                          </span>
                        </td>
                        <td>
                          <div className="small fw-bold text-muted">
                             {new Date(v.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                          </div>
                        </td>
                        <td className="text-end pe-4">
                          <div className="d-flex justify-content-end gap-1">
                            <Link to={`/vendor-profile/${v.id}`} className="btn btn-action btn-blue" title="Vendor Profile"><FaUser  /></Link>
                            <Link to={`/vendor-view/${v.uuid}`} className="btn btn-action btn-purple" title="View Inventory"><FaEye /></Link>
                            <button onClick={() => toggleStatus(v.id, v.status)} className={`btn btn-action ${v.status === 'active' ? 'btn-orange' : 'btn-green'}`} title={v.status === "active" ? "Disable Account" : "Enable Account"}><FaBan /></button>
                            <button onClick={() => deleteVendor(v.id)} className="btn btn-action btn-red" title="Delete Merchant"><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="6" className="text-center py-5 text-muted">No merchants match your current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-footer bg-white py-3 border-0">
                <small className="text-muted">Total Results: <strong>{filteredVendors.length}</strong> vendors found.</small>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; line-height: 1.2; }
        
        .vendor-table tbody td { padding: 12px 10px; border-bottom: 1px solid #f6f9fc; }
        
        .avatar-circle { width: 32px; height: 32px; background: #6366f1; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 13px; }
        
        /* Status Badges */
        .status-badge { padding: 4px 10px; border-radius: 20px; font-size: 10px; font-weight: 700; text-transform: uppercase; display: inline-flex; align-items: center; }
        .status-badge.active { background: #dcfce7; color: #15803d; }
        .status-badge.inactive { background: #fee2e2; color: #b91c1c; }

        /* Action Buttons */
        .btn-action { width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 6px; border: none; font-size: 12px; transition: 0.2s; }
        .btn-blue { background: #eff6ff; color: #2563eb; }
        .btn-purple { background: #f5f3ff; color: #7c3aed; }
        .btn-orange { background: #fff7ed; color: #ea580c; }
        .btn-green { background: #f0fdf4; color: #16a34a; }
        .btn-red { background: #fef2f2; color: #dc2626; }
        
        .btn-action:hover { filter: brightness(0.9); transform: translateY(-1px); }
        .rounded-4 { border-radius: 1rem !important; }
        .stat-card { min-width: 160px; }
      `}</style>
    </>
  );
}

export default AllVendor;